import { getAuth } from 'firebase/auth'
import * as Sentry from '@sentry/browser'
import { AnalysisRunSchema } from '../analysis/schemas'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { readResponseJson, getResponseDebugContext } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/** Persist a failed browser upload on its table attachment. */
export async function postTableDocumentUploadFailure(tableId: string, documentId: string) {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error('Sign in to access tables.')
  const url = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/tables/${tableId}/documents/${documentId}/upload-failure`
  const response = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  })
  const { data, parseError } = await readResponseJson(response)
  if (!response.ok) {
    if (response.status >= 500) Sentry.captureException(new Error('Table analysis request failed'), { extra: getResponseDebugContext(response, url) })
    const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined
    const approvedMessages = [
      'Matter not found',
      'Table resource not found.',
      'Table creator access required',
      'Select available documents in this matter.',
      'Select at least one document in this matter.',
    ]
    throw new ApiError(
      response.status,
      response.status >= 400 && response.status < 500 && typeof detail === 'string' && approvedMessages.includes(detail)
        ? detail
        : 'Unable to save the upload failure.'
    )
  }
  const parsed = AnalysisRunSchema.safeParse(data)
  if (parseError || !parsed.success) {
    Sentry.captureException(new Error('Invalid analysis response'), { extra: getResponseDebugContext(response, url) })
    throw new ApiResponsePayloadError(response.status, 'The analysis response could not be read. Please retry.')
  }
  return parsed.data
}
